import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import { PRODUCTS } from "../data/products";

const CATEGORIES = [
  { key: "all", label: "All Spices" },
  { key: "powder", label: "Ground Spices", match: (p) => /powder|masala/i.test(p.name) },
  { key: "seeds", label: "Whole Seeds", match: (p) => /seeds/i.test(p.name) },
  { key: "premium", label: "Premium & Exotic", match: (p) => /cardamom|mace|nutmeg|pepper|cinnamon|cloves/i.test(p.name) },
];

export default function Products() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const filtered = useMemo(() => {
    let list = PRODUCTS;
    if (cat !== "all") {
      const c = CATEGORIES.find((x) => x.key === cat);
      if (c?.match) list = list.filter(c.match);
    }
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(s) || p.tagline.toLowerCase().includes(s));
    }
    return list;
  }, [q, cat]);

  return (
    <>
      <PageHeader
        eyebrow="The Catalogue"
        title="Eighteen spices. One export standard."
        italicWord="Eighteen"
        subtitle="Ground powders, whole seeds and signature blends — each processed to laboratory-verified export specifications and available in bulk, retail or private-label formats."
      />

      {/* Filter Bar */}
      <section className="bg-brand-ivory sticky top-20 z-30 border-b border-brand-charcoal/10 backdrop-blur-md">
        <div className="container-luxe py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <div className="flex items-center gap-2 flex-1">
            <Search className="w-4 h-4 text-brand-charcoal/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search spices — turmeric, cardamom, garam masala…"
              className="flex-1 bg-transparent border-0 outline-none text-[14px] py-2 placeholder-brand-charcoal/40"
              data-testid="products-search"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCat(c.key)}
                data-testid={`products-cat-${c.key}`}
                className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold transition-all border ${
                  cat === c.key
                    ? "bg-brand-green text-brand-ivory border-brand-green"
                    : "bg-transparent text-brand-charcoal/70 border-brand-charcoal/15 hover:border-brand-green hover:text-brand-green"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24 bg-brand-ivory">
        <div className="container-luxe">
          {filtered.length === 0 && (
            <div className="text-center py-24 text-brand-charcoal/50">No spices matched your search.</div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={i * 50}>
                <Link to={`/products/${p.slug}`} className="group block" data-testid={`products-card-${p.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-brand-ink">
                    <div className="absolute inset-0" style={{ backgroundColor: p.color, opacity: 0.35 }} />
                    <img src={p.image} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/75 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-brand-ivory/80 border border-brand-ivory/30 px-2.5 py-1">
                      {p.grade.split("/")[0].trim()}
                    </div>
                    <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-brand-ivory">
                      <div>
                        <div className="font-serif text-2xl leading-tight">{p.name}</div>
                        <div className="text-[11px] tracking-[0.22em] uppercase text-brand-ivory/60 italic mt-1">{p.tagline}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-[12px] tracking-wide">
                    <span className="text-brand-charcoal/60">MOQ · {p.moq}</span>
                    <span className="text-brand-green font-semibold uppercase tracking-[0.2em] text-[11px]">View Details</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
